import { Box3, BufferAttribute, BufferGeometry, Sphere, Vector3 } from "three/webgpu";

/** 小标注的起始顶点容量；后续只按倍数扩容，不随短长布局来回收缩。 */
const INITIAL_VERTEX_CAPACITY = 16;

/**
 * 尺寸显示专用的 position 缓冲所有者。
 *
 * 同容量原位更新，扩容时释放整个旧几何并发布新几何，避免替换 attribute 后失去
 * 渲染器的销毁入口。有效顶点由 drawRange 和独立边界限定，不把容量尾部作为几何。
 */
export class SketchDimensionGeometryBuffer {
	/** 当前拥有的几何；扩容后由调用方在同步更新结束时重新赋给显示对象。 */
	private currentGeometry = this.createGeometry(INITIAL_VERTEX_CAPACITY);
	/** 本次更新的有效顶点数，不等于已分配容量。 */
	private vertexCount = 0;
	/** 边界计算复用点，避免按顶点创建临时向量。 */
	private readonly boundsPoint = new Vector3();
	/** 生命周期结束后不允许重新申请资源。 */
	private disposed = false;

	/** @returns 当前拥有的显示几何；调用方不得单独替换其 position 或负责销毁。 */
	public get geometry(): BufferGeometry {
		return this.currentGeometry;
	}

	/**
	 * 预留本次有效顶点区间，并返回可原位写入的属性。
	 * @param vertexCount 有效顶点数。
	 * @returns 可用 setXYZ 写入的 position 属性。
	 */
	public beginUpdate(vertexCount: number): BufferAttribute {
		if (this.disposed) throw new Error("尺寸几何缓冲已释放，不能继续写入。");
		if (!Number.isSafeInteger(vertexCount) || vertexCount < 0) throw new Error(`尺寸顶点数量无效：${vertexCount}。`);
		let attribute = this.currentGeometry.getAttribute("position") as BufferAttribute;
		if (vertexCount > attribute.count) {
			let capacity = attribute.count;
			while (capacity < vertexCount) capacity *= 2;
			const nextGeometry = this.createGeometry(capacity);
			// 必须在旧 position 仍可达、旧 RenderObject 尚未改绑时销毁，不能仅替换属性。
			this.currentGeometry.dispose();
			this.currentGeometry = nextGeometry;
			attribute = nextGeometry.getAttribute("position") as BufferAttribute;
		}
		this.vertexCount = vertexCount;
		return attribute;
	}

	/**
	 * 发布有效绘制区间、上传版本及边界；随后调用方把返回值赋给显示对象 geometry。
	 * @returns 已写入完整数据的当前几何。
	 */
	public endUpdate(): BufferGeometry {
		if (this.disposed) throw new Error("尺寸几何缓冲已释放，不能发布更新。");
		const geometry = this.currentGeometry;
		const attribute = geometry.getAttribute("position") as BufferAttribute;
		geometry.setDrawRange(0, this.vertexCount);
		// 一次更新总是覆盖有效前缀；同帧缩短后尾部不参与绘制，后续变长会再次完整写入。
		attribute.clearUpdateRanges();
		if (this.vertexCount > 0) {
			attribute.addUpdateRange(0, this.vertexCount * 3);
			attribute.needsUpdate = true;
		}
		const box = geometry.boundingBox!;
		const sphere = geometry.boundingSphere!;
		box.makeEmpty();
		sphere.makeEmpty();
		for (let index = 0; index < this.vertexCount; index++) {
			box.expandByPoint(this.boundsPoint.fromBufferAttribute(attribute, index));
		}
		if (this.vertexCount > 0) {
			box.getCenter(sphere.center);
			let radiusSquared = 0;
			for (let index = 0; index < this.vertexCount; index++) {
				radiusSquared = Math.max(radiusSquared, sphere.center.distanceToSquared(this.boundsPoint.fromBufferAttribute(attribute, index)));
			}
			sphere.radius = Math.sqrt(radiusSquared);
		}
		return geometry;
	}

	/** 幂等释放当前几何，旧扩容几何已经在替换前释放。 */
	public dispose(): void {
		if (this.disposed) return;
		this.disposed = true;
		this.currentGeometry.dispose();
	}

	/** @param capacity 顶点容量。 @returns 独占 position 及边界的非索引几何。 */
	private createGeometry(capacity: number): BufferGeometry {
		const geometry = new BufferGeometry();
		// 使用版本控制上传；当前 Three.js 的 DynamicDrawUsage 会使静态标注每帧重复上传。
		geometry.setAttribute("position", new BufferAttribute(new Float32Array(capacity * 3), 3));
		geometry.setDrawRange(0, 0);
		geometry.boundingBox = new Box3();
		geometry.boundingSphere = new Sphere().makeEmpty();
		return geometry;
	}
}
